import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { ImportResult } from './types';
import { ScrollArea } from '@/components/ui/scroll-area';

interface ImportResultCardProps {
  result: ImportResult;
}

export const ImportResultCard = ({ result }: ImportResultCardProps) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          Resumen de importación
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="text-center p-3 bg-muted rounded-lg">
            <div className="text-2xl font-bold text-muted-foreground">{result.total}</div>
            <div className="text-sm text-muted-foreground">Total</div>
          </div>
          <div className="text-center p-3 bg-success/10 rounded-lg">
            <div className="text-2xl font-bold text-success">{result.imported.length}</div>
            <div className="text-sm text-success">Importados</div>
          </div>
          <div className="text-center p-3 bg-yellow-500/10 rounded-lg">
            <div className="text-2xl font-bold text-yellow-600">{result.duplicated.length}</div>
            <div className="text-sm text-yellow-600">Duplicados</div>
          </div>
          <div className="text-center p-3 bg-destructive/10 rounded-lg">
            <div className="text-2xl font-bold text-destructive">{result.errors.length}</div>
            <div className="text-sm text-destructive">Errores</div>
          </div>
        </div>
        
        <ScrollArea className="h-[300px] pr-4">
          {result.imported.length > 0 && (
            <div className="mb-4 p-4 bg-success/10 rounded-lg border border-success/20">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-4 h-4 text-success" />
                <div className="font-medium text-success">Alumnos importados correctamente:</div>
              </div>
              <div className="space-y-2">
                {result.imported.map((s, i) => (
                  <div key={i} className="text-sm pl-6 text-success/80">
                    {s.nombre} ({s.clase})
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {result.duplicated.length > 0 && (
            <div className="mb-4 p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="w-4 h-4 text-yellow-600" />
                <div className="font-medium text-yellow-600">Alumnos duplicados:</div>
              </div>
              <div className="space-y-2">
                {result.duplicated.map((d, i) => (
                  <div key={i} className="text-sm pl-6 text-yellow-600/80">
                    <span className="font-medium">{d.nombre}</span> ({d.clase})
                    <div className="text-xs text-yellow-600/70 mt-0.5">{d.motivo}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {result.errors.length > 0 && (
            <div className="mb-4 p-4 bg-destructive/10 rounded-lg border border-destructive/20">
              <div className="flex items-center gap-2 mb-2">
                <XCircle className="w-4 h-4 text-destructive" />
                <div className="font-medium text-destructive">Errores durante la importación:</div>
              </div>
              <div className="space-y-2">
                {result.errors.map((e, i) => (
                  <div key={i} className="text-sm pl-6 text-destructive/80">
                    <span className="font-medium">{e.nombre}</span> ({e.clase})
                    <div className="text-xs text-destructive/70 mt-0.5">{e.motivo}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
};